/**
 * CourtAuditDrawer — Phase 178.6.H.
 *
 * Right-side slide drawer showing the immutable lifecycle history for one
 * court.  Backed by `GET /api/arenax/host/venue/{vid}/courts/{cid}/audit`.
 *
 * Doctrine
 * ────────
 *   • CALM — not log-dump chaos.  Sparse spacing.  One event per row.
 *   • FORENSIC — every entry shows actor, ISO time, before → after, cascade
 *   • TRUSTWORTHY — never editable; newest first; immutable
 *
 * Filters
 * ───────
 *   • All / Lifecycle / Maintenance / Deletion
 *     (no per-property filtering — keeps the surface honest)
 */
import { useEffect, useState, useMemo } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  History, Archive, EyeOff, Wrench, Trash2, RotateCcw, AlertTriangle,
  Loader2,
} from 'lucide-react';
import api from '@/lib/api';
import { track } from '@/lib/analytics';

// Map event → icon + label + tone + filter bucket
const EVENT_META = {
  court_hidden:         { icon: EyeOff,         label: 'Hidden from public', tone: '#6366f1', filter: 'lifecycle' },
  court_archived:       { icon: Archive,        label: 'Archived',           tone: '#64748b', filter: 'lifecycle' },
  court_restored:       { icon: RotateCcw,      label: 'Restored',           tone: '#10b981', filter: 'lifecycle' },
  maintenance_entered:  { icon: Wrench,         label: 'Maintenance entered', tone: '#f59e0b', filter: 'maintenance' },
  maintenance_exited:   { icon: Wrench,         label: 'Maintenance exited',  tone: '#10b981', filter: 'maintenance' },
  court_deleted_soft:   { icon: AlertTriangle,  label: 'Soft-deleted',       tone: 'hsl(0 70% 55%)', filter: 'deletion' },
  court_deleted_hard:   { icon: Trash2,         label: 'Deleted permanently', tone: 'hsl(0 70% 55%)', filter: 'deletion' },
};

const FILTER_CHIPS = [
  { id: 'all',          label: 'All' },
  { id: 'lifecycle',    label: 'Lifecycle' },
  { id: 'maintenance',  label: 'Maintenance' },
  { id: 'deletion',     label: 'Deletion' },
];

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} · ${timePart}`;
  } catch (_) { return iso; }
}

function EventRow({ event }) {
  const meta = EVENT_META[event.event] || { icon: History, label: event.event, tone: '#64748b' };
  const Icon = meta.icon;
  const actorLabel = event.actor_email || event.actor_user_id || 'system';
  const cascade = event.meta?.cascade_slots_affected;
  const reason = event.meta?.reason;
  return (
    <div
      className="relative pl-8 pb-5 last:pb-1"
      data-testid={`court-audit-row-${event.id}`}
    >
      {/* Timeline line */}
      <div
        className="absolute left-[10px] top-3 bottom-0 w-px"
        style={{ background: 'var(--ax-border-quiet)' }}
        aria-hidden
      />
      {/* Dot */}
      <div
        className="absolute left-0 top-0.5 h-5 w-5 rounded-full flex items-center justify-center"
        style={{ background: `${meta.tone}1a`, border: `1.5px solid ${meta.tone}` }}
      >
        <Icon className="h-2.5 w-2.5" style={{ color: meta.tone }} />
      </div>

      <div className="space-y-0.5">
        <div className="text-sm font-bold" style={{ color: 'hsl(var(--ax-text))' }}>
          {meta.label}
        </div>
        <div className="text-[11px] font-mono" style={{ color: 'hsl(var(--ax-text-muted))' }}>
          {formatTimestamp(event.occurred_at)}
        </div>
        <div className="text-[11px]" style={{ color: 'hsl(var(--ax-text-muted))' }}>
          by <span className="font-medium" style={{ color: 'hsl(var(--ax-text))' }}>{actorLabel}</span>
        </div>

        {/* Transition strip */}
        {event.before_status && event.after_status && event.before_status !== event.after_status && (
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono mt-1 px-2 py-0.5 rounded-md"
               style={{ background: 'hsl(var(--muted) / 0.5)', color: 'hsl(var(--muted-foreground))' }}>
            <span>{event.before_status}</span>
            <span style={{ color: meta.tone }}>→</span>
            <span style={{ color: meta.tone }}>{event.after_status}</span>
          </div>
        )}

        {cascade > 0 && (
          <div className="text-[11px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            <span className="font-bold" style={{ color: 'hsl(var(--ax-text))' }}>{cascade}</span>{' '}
            future {cascade === 1 ? 'slot' : 'slots'} cascaded
          </div>
        )}

        {reason && (
          <div
            className="text-[11px] italic mt-1 pl-2 border-l-2"
            style={{ color: 'hsl(var(--ax-text-muted))', borderColor: 'var(--ax-border-quiet)' }}
          >
            {reason}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CourtAuditDrawer({ court, venueId, open, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!court || !open) return;
    let cancelled = false;
    setLoading(true);
    track('drawer_opened', { drawer: 'court_audit', court_id: court.id });
    api.get(`/arenax/host/venue/${venueId}/courts/${court.id}/audit?limit=100`)
      .then((r) => {
        if (cancelled) return;
        setEvents(r.data?.events || []);
      })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [court, open, venueId]);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => {
      const meta = EVENT_META[e.event];
      return meta?.filter === filter;
    });
  }, [events, filter]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col"
        data-testid="court-audit-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Lifecycle history
          </SheetTitle>
          <SheetDescription className="leading-relaxed">
            Immutable forensic ledger for <strong>{court?.name}</strong>.
            Every state change is recorded with actor, timestamp, and
            cascade effect. Newest first.
          </SheetDescription>
        </SheetHeader>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 pt-3 pb-2" data-testid="court-audit-filters">
          {FILTER_CHIPS.map((c) => (
            <Button
              key={c.id}
              size="sm"
              type="button"
              variant={filter === c.id ? 'default' : 'outline'}
              onClick={() => setFilter(c.id)}
              data-testid={`court-audit-filter-${c.id}`}
            >
              {c.label}
            </Button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto pt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-xs"
                 style={{ color: 'hsl(var(--ax-text-muted))' }}>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Loading history…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="text-center py-12 text-xs italic"
              style={{ color: 'hsl(var(--ax-text-faint))' }}
              data-testid="court-audit-empty"
            >
              {events.length === 0
                ? 'No lifecycle events yet.  This court is brand new.'
                : `No ${filter} events.  Try a different filter.`}
            </div>
          ) : (
            <div className="pt-2" data-testid="court-audit-list">
              {filtered.map((e) => <EventRow key={e.id} event={e} />)}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
